// src/App.jsx

import React, {
  useEffect,
} from 'react';

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import {
  Toaster,
} from 'react-hot-toast';

import {
  Network,
} from '@capacitor/network';

import {
  Capacitor,
} from '@capacitor/core';

import {
  AuthProvider,
  useAuth,
} from './context/AuthContext';

import Layout from './components/Layout';

import LandingPage from './pages/LandingPage';
import Library from './pages/library/Library';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Books from './pages/Books';
import Favorites from './pages/Favorites';
import DeleteAccount from './pages/DeleteAccount';
import PrivacyPolicy from './pages/PrivacyPolicy';

import {
  sendSyncQueue,
} from './utils/sync';

import apiClient from './api/client';

import './index.css';

// =======================================================
// SESSION LOADER
// =======================================================

function SessionLoader() {
  return (
    <div
      className="
        min-h-screen bg-gray-50
        flex items-center justify-center px-4
        [padding-top:env(safe-area-inset-top)]
        [padding-bottom:env(safe-area-inset-bottom)]
      "
    >
      <img
        src="/assets/app-logo.png"
        alt="PawaLibrary"
        className="h-20 w-20 object-contain"
      />
    </div>
  );
}

// =======================================================
// PROTECTED APP
// =======================================================

function ProtectedApp() {
  const {
    loading,
    isAuthenticated,
  } = useAuth();

  const location = useLocation();

  if (loading) {
    return <SessionLoader />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location,
        }}
      />
    );
  }

  return (
    <Layout>
      <Routes>
        <Route
          index
          element={<Library />}
        />

        <Route
          path="settings"
          element={<Settings />}
        />

        <Route
          path="books"
          element={<Books />}
        />

        <Route
          path="favorites"
          element={<Favorites />}
        />

        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />
      </Routes>
    </Layout>
  );
}

// =======================================================
// ROOT ROUTE
// =======================================================

function RootRoute() {
  const {
    loading,
    isAuthenticated,
  } = useAuth();

  if (loading) {
    return <SessionLoader />;
  }

  if (isAuthenticated) {
    return (
      <Layout>
        <Library />
      </Layout>
    );
  }

  if (
    Capacitor.isNativePlatform()
  ) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  return <LandingPage />;
}

// =======================================================
// GUEST-ONLY ROUTES
// =======================================================

function GuestOnly({ children }) {
  const {
    loading,
    isAuthenticated,
  } = useAuth();

  if (loading) {
    return <SessionLoader />;
  }

  if (isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return children;
}

// =======================================================
// APPLICATION ROUTES
// =======================================================

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={<RootRoute />}
      />

      <Route
        path="/login"
        element={
          <GuestOnly>
            <Login />
          </GuestOnly>
        }
      />

      <Route
        path="/register"
        element={
          <GuestOnly>
            <Register />
          </GuestOnly>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <GuestOnly>
            <ForgotPassword />
          </GuestOnly>
        }
      />

      <Route
        path="/reset-password"
        element={
          <GuestOnly>
            <ResetPassword />
          </GuestOnly>
        }
      />

      {/*
        Public privacy-policy page.

        Google Play reviewers and users must be
        able to open this page without signing in.
      */}
      <Route
        path="/privacy-policy"
        element={<PrivacyPolicy />}
      />

      {/*
        Public account-deletion page.

        Google Play reviewers and users must be
        able to open this page without signing in.
      */}
      <Route
        path="/delete-account"
        element={<DeleteAccount />}
      />

      {/* All remaining routes require authentication. */}
      <Route
        path="/*"
        element={<ProtectedApp />}
      />
    </Routes>
  );
}

// =======================================================
// APP
// =======================================================

function App() {
  useEffect(() => {
    let listenerHandle = null;
    let disposed = false;

    const setupNetworkListener =
      async () => {
        try {
          const handle =
            await Network.addListener(
              'networkStatusChange',
              async (status) => {
                if (
                  !status.connected
                ) {
                  return;
                }

                try {
                  await sendSyncQueue(
                    apiClient
                  );
                } catch (error) {
                  console.error(
                    'Sync failed:',
                    error
                  );
                }
              }
            );

          if (disposed) {
            await handle.remove();
          } else {
            listenerHandle = handle;
          }
        } catch (error) {
          console.error(
            'Failed to set up network listener:',
            error
          );
        }
      };

    setupNetworkListener();

    return () => {
      disposed = true;

      if (
        listenerHandle?.remove
      ) {
        Promise.resolve(
          listenerHandle.remove()
        ).catch((error) => {
          console.error(
            'Failed to remove network listener:',
            error
          );
        });
      }
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: '12px',
              maxWidth:
                'min(92vw, 420px)',
            },
          }}
        />

        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;